import {
  formatDateDisplay,
  formatDateTimeDisplay,
  parseDateFromISO,
  addDays,
  isDateInPast,
} from '@applicator/shared'
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

import DatePicker from './DatePicker/DatePicker'
import { bindAnchoredOverlayPosition } from './DatePicker/anchorPositioning'
import { handleDateSelect, handleQuickAction } from './JobDateChips/handlers'
import { isTimestampField, computeEffectiveDate, resolveChipPickerLeft } from './JobDateChips/helpers'
import { DateFieldConfig, JobDatesData, DateMode, UserRules } from './JobDateChips/types'

export type { DateFieldConfig, JobDatesData, DateMode, UserRules }

interface JobDateChipsProps {
  jobId: string
  initialJobDates: JobDatesData
  userRules?: UserRules
  onUpdated?: (updatedDates: Partial<JobDatesData>) => void
  layout?: 'horizontal' | 'vertical' | 'grid' // Layout direction
}

/**
 * Effective date calculation:
 * - if mode === 'manual' -> use *_manual value
 * - if mode === 'cleared' -> return null (show "Not set")
 * - else (auto) -> compute from rules using getAutoDate()
 */
function JobDateChips({ jobId, initialJobDates, userRules = {}, onUpdated, layout = 'horizontal' }: JobDateChipsProps) {
  // Ensure initialJobDates has all required fields with defaults
  const safeInitialDates: JobDatesData = {
    saved_at: initialJobDates?.saved_at ?? null,
    deadline_at: initialJobDates?.deadline_at ?? null,
    applied_at: initialJobDates?.applied_at ?? null,
    follow_up_at: initialJobDates?.follow_up_at ?? null,
    date_saved_mode: initialJobDates?.date_saved_mode ?? 'auto',
    deadline_mode: initialJobDates?.deadline_mode ?? 'auto',
    date_applied_mode: initialJobDates?.date_applied_mode ?? 'auto',
    follow_up_mode: initialJobDates?.follow_up_mode ?? 'auto',
    date_saved_manual: initialJobDates?.date_saved_manual ?? null,
    deadline_manual: initialJobDates?.deadline_manual ?? null,
    date_applied_manual: initialJobDates?.date_applied_manual ?? null,
    follow_up_manual: initialJobDates?.follow_up_manual ?? null,
  }
  
  const [jobDates, setJobDates] = useState<JobDatesData>(safeInitialDates)
  const [openField, setOpenField] = useState<string | null>(null)
  const [pickerAnchor, setPickerAnchor] = useState<{ top: number; left: number; width: number } | null>(null)
  const chipRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})
  const datePickerRef = useRef<HTMLDivElement | null>(null)
  
  // Update state when initialJobDates changes
  useEffect(() => {
    if (initialJobDates) {
      setJobDates({
        saved_at: initialJobDates.saved_at ?? null,
        deadline_at: initialJobDates.deadline_at ?? null,
        applied_at: initialJobDates.applied_at ?? null,
        follow_up_at: initialJobDates.follow_up_at ?? null,
        date_saved_mode: initialJobDates.date_saved_mode ?? 'auto',
        deadline_mode: initialJobDates.deadline_mode ?? 'auto',
        date_applied_mode: initialJobDates.date_applied_mode ?? 'auto',
        follow_up_mode: initialJobDates.follow_up_mode ?? 'auto',
        date_saved_manual: initialJobDates.date_saved_manual ?? null,
        deadline_manual: initialJobDates.deadline_manual ?? null,
        date_applied_manual: initialJobDates.date_applied_manual ?? null,
        follow_up_manual: initialJobDates.follow_up_manual ?? null,
      })
    }
  }, [jobId, initialJobDates])

  // Keep portaled picker anchored to the open chip on scroll/resize (all overflow ancestors).
  useEffect(() => {
    if (!openField) {
      setPickerAnchor(null)
      return
    }

    const fieldKey = openField
    const isTimestamp = isTimestampField(fieldKey)
    const pickerWidth = isTimestamp ? 600 : 280

    return bindAnchoredOverlayPosition({
      getTrigger: () => chipRefs.current[fieldKey] ?? null,
      getPicker: () => datePickerRef.current,
      options: {
        includeTime: isTimestamp,
        pickerWidth,
        resolveLeft: (rect, width, viewportWidth) =>
          resolveChipPickerLeft(rect, width, viewportWidth, isTimestamp),
      },
      onPosition: (position) => {
        setPickerAnchor({
          top: position.top,
          left: position.left,
          width: position.width,
        })
      },
    })
  }, [openField])

  // Close popover on outside click
  useEffect(() => {
    if (!openField) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        !datePickerRef.current?.contains(event.target as Node) &&
        !chipRefs.current[openField || '']?.contains(event.target as Node)
      ) {
        setOpenField(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openField])

  // Handle ESC key
  useEffect(() => {
    if (!openField) return

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenField(null)
      }
    }

    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('keydown', handleEsc)
    }
  }, [openField])

  // Don't render if jobId is missing
  if (!jobId) {
    return null
  }

  // Field configurations
  const fieldConfigs: DateFieldConfig[] = [
    {
      key: 'date_saved',
      label: 'Date Saved',
      getAutoDate: (job, _rules) => {
        // Always use saved_at - this is set automatically when job is created
        // rules parameter is required by type but not used here
        return parseDateFromISO(job.saved_at)
      },
      isOverdue: () => false, // Date saved is never overdue
      allowAutoComputeWhenMissingBase: true,
      isReadOnly: true, // Date Saved is not editable
    },
    {
      key: 'date_applied',
      label: 'Date Applied',
      getAutoDate: () => {
        // Usually null - do not guess
        return null
      },
      isOverdue: () => false,
      allowAutoComputeWhenMissingBase: false,
    },
    {
      key: 'follow_up',
      label: 'Follow Up',
      getAutoDate: (job, _rules) => {
        const appliedDate = parseDateFromISO(job.applied_at)
        if (!appliedDate) return null
        const days = _rules.follow_up_days_after_applied ?? 7
        return addDays(appliedDate, days)
      },
      isOverdue: (date) => isDateInPast(date),
      allowAutoComputeWhenMissingBase: false,
    },
    {
      key: 'deadline',
      label: 'Deadline',
      getAutoDate: (job, rules) => {
        const savedDate = parseDateFromISO(job.saved_at)
        if (!savedDate) return null
        const days = rules.deadline_days_after_saved ?? 14
        return addDays(savedDate, days)
      },
      isOverdue: (date) => isDateInPast(date),
      allowAutoComputeWhenMissingBase: false,
    },
  ]



  // Handle chip click
  const handleChipClick = (fieldKey: string, event: React.MouseEvent<HTMLButtonElement>) => {
    // Date Saved is read-only, don't allow editing
    if (fieldKey === 'date_saved') {
      return
    }
    
    event.stopPropagation()
    setOpenField(openField !== fieldKey ? fieldKey : null)
  }


  const handleDateSelectWrapper = async (fieldKey: string, dateStr: string) => {
    if (!jobId) {
      console.error('Cannot update date field: jobId is missing')
      return
    }
    if (typeof setOpenField !== 'function') {
      console.error('Cannot update date field: setOpenField is not a function', { setOpenField, jobId })
      return
    }
    await handleDateSelect(fieldKey, dateStr, jobId, jobDates, userRules, fieldConfigs, setJobDates, setOpenField, onUpdated)
  }

  const handleQuickActionWrapper = async (fieldKey: string, action: string) => {
    await handleQuickAction(fieldKey, action, jobId, jobDates, userRules, fieldConfigs, setJobDates, setOpenField, onUpdated)
  }

  return (
    <div className={
      layout === 'vertical' ? 'flex flex-col gap-2' : 
      layout === 'grid' ? 'grid grid-cols-2 gap-2' : 
      'flex flex-wrap gap-2 mb-4'
    }>
      {fieldConfigs.map((field) => {
        const effectiveDate = computeEffectiveDate(field, jobDates, userRules)
        const modeKey = `${field.key}_mode` as keyof JobDatesData
        const mode = (jobDates[modeKey] as DateMode | undefined) || 'auto'
        const isOverdue = field.isOverdue(effectiveDate)
        const isOpen = openField === field.key
        const isReadOnly = field.isReadOnly || false

        return (
          <div key={field.key} className="relative">
            <button
              ref={(el) => (chipRefs.current[field.key] = el)}
              onClick={(e) => handleChipClick(field.key, e)}
              disabled={isReadOnly}
              className={`
                ${layout === 'vertical' || layout === 'grid' ? 'px-2 py-0.5' : 'px-3 py-1.5'} rounded-full transition-all duration-200
                border flex items-center gap-1
                ${isOpen
                  ? 'border-teal-600 bg-teal-50'
                  : isOverdue
                  ? 'border-orange-300 bg-orange-50'
                  : 'border-gray-300 bg-white'
                }
                ${isReadOnly 
                  ? 'cursor-default opacity-75' 
                  : 'cursor-pointer hover:border-gray-400 hover:bg-gray-50'
                }
              `}
            >
              {/* Label - заголовок */}
              <span className={`
                ${layout === 'grid' ? 'text-[10px]' : 'text-xs'} font-semibold uppercase tracking-wide
                ${isOpen
                  ? 'text-teal-700'
                  : isOverdue
                  ? 'text-orange-700'
                  : 'text-gray-500'
                }
              `}>
                {field.label}
              </span>
              
              {/* Separator */}
              <span className={`
                w-px h-4
                ${isOpen
                  ? 'bg-teal-300'
                  : isOverdue
                  ? 'bg-orange-300'
                  : 'bg-gray-300'
                }
              `}></span>
              
              {/* Date value - дата */}
              <span className={`
                ${layout === 'grid' ? 'text-xs' : 'text-sm'} font-medium
                ${isOpen
                  ? 'text-teal-900'
                  : isOverdue
                  ? 'text-orange-900'
                  : 'text-gray-900'
                }
              `}>
                {effectiveDate 
                  ? (isTimestampField(field.key) 
                      ? formatDateTimeDisplay(effectiveDate.toISOString())
                      : formatDateDisplay(effectiveDate.toISOString()))
                  : 'Not set'}
              </span>
              
              {/* Separator */}
              <span className={`
                w-px h-4
                ${isOpen
                  ? 'bg-teal-300'
                  : isOverdue
                  ? 'bg-orange-300'
                  : 'bg-gray-300'
                }
              `}></span>
              
              {/* Badge - Auto/Manual/Cleared (hidden for read-only Date Saved) */}
              {!isReadOnly && (
                <span
                  className={`
                    ${layout === 'grid' ? 'px-1 py-0' : 'px-1.5 py-0.5'} rounded ${layout === 'grid' ? 'text-[10px]' : 'text-xs'} font-semibold
                    ${mode === 'manual'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                    }
                  `}
                >
                  {mode === 'manual' ? 'Manual' : mode === 'cleared' ? 'Cleared' : 'Auto'}
                </span>
              )}
            </button>

            {isOpen && pickerAnchor && (
              <>
                {(() => {
                  const datePicker = (
                    <DatePicker
                      ref={(node) => {
                        datePickerRef.current = node
                      }}
                      value={
                        effectiveDate
                          ? (isTimestampField(field.key)
                              ? formatDateTimeDisplay(effectiveDate.toISOString())
                              : formatDateDisplay(effectiveDate.toISOString()))
                          : 'Not set'
                      }
                      includeTime={isTimestampField(field.key)}
                      onChange={(dateStr) => {
                        // For date-only fields, handle immediately
                        if (!isTimestampField(field.key)) {
                          if (dateStr === 'N/A' || dateStr === 'Not set') {
                            // Don't clear - date cannot be removed
                            return
                          } else {
                            // Parse DD/MM/YYYY and convert to ISO
                            const dateParts = dateStr.split('/')
                            if (dateParts.length === 3) {
                              const year = parseInt(dateParts[2])
                              const month = parseInt(dateParts[1]) - 1
                              const day = parseInt(dateParts[0])
                              const date = new Date(year, month, day)
                              handleDateSelectWrapper(field.key, date.toISOString())
                            }
                          }
                        }
                        // For timestamp fields, onChange is not called until Confirm
                      }}
                      onConfirm={isTimestampField(field.key) ? (dateStr) => {
                        // Handle confirmation for timestamp fields
                        if (dateStr === 'N/A' || dateStr === 'Not set') {
                          // Don't clear - date cannot be removed
                          return
                        } else {
                          // Parse DD/MM/YYYY, HH:MM and convert to ISO
                          const parts = dateStr.split(',')
                          const datePart = parts[0].trim()
                          const timePart = parts[1]?.trim() || null
                          const dateParts = datePart.split('/')
                          
                          if (dateParts.length === 3) {
                            const year = parseInt(dateParts[2])
                            const month = parseInt(dateParts[1]) - 1
                            const day = parseInt(dateParts[0])
                            
                            let date: Date
                            if (timePart) {
                              // Parse time HH:MM
                              const timeParts = timePart.split(':')
                              if (timeParts.length === 2) {
                                const hours = parseInt(timeParts[0])
                                const minutes = parseInt(timeParts[1])
                                date = new Date(year, month, day, hours, minutes)
                              } else {
                                // Fallback to current time
                                const now = new Date()
                                date = new Date(year, month, day, now.getHours(), now.getMinutes())
                              }
                            } else {
                              // Use current time for timestamp fields
                              const now = new Date()
                              date = new Date(year, month, day, now.getHours(), now.getMinutes())
                            }
                            
                            handleDateSelectWrapper(field.key, date.toISOString())
                          }
                        }
                      } : undefined}
                      onQuickAction={(action) => {
                        handleQuickActionWrapper(field.key, action)
                      }}
                      showRevert={mode !== 'auto'}
                      onClose={() => {
                        setOpenField(null)
                      }}
                      position={pickerAnchor}
                    />
                  )
                  // Render DatePicker via Portal to avoid transform issues
                  return typeof document !== 'undefined' 
                    ? createPortal(datePicker, document.body)
                    : datePicker
                })()}
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default JobDateChips

